import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Header from "./Header";

const BASE_URL =
  "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Coupon";

function AddCoupon() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    code: "",
    discount_type: "percentage",
    discount_value: "",
    description: "",
    min_purchase_amount: "",
    max_discount_amount: "",
    start_date: "",
    end_date: "",
    usage_limit: "",
  });

  // ── Field handler ─────────────────────────────────────────────────────────
  const handleField = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!e.target.checkValidity()) {
      e.target.classList.add("was-validated");
      return;
    }

    const payload = {
      code: form.code.trim().toUpperCase(),
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value),
      description: form.description.trim(),
      start_date: form.start_date,
      end_date: form.end_date,
      ...(form.min_purchase_amount !== "" && {
        min_purchase_amount: Number(form.min_purchase_amount),
      }),
      ...(form.max_discount_amount !== "" && {
        max_discount_amount: Number(form.max_discount_amount),
      }),
      ...(form.usage_limit !== "" && {
        usage_limit: Number(form.usage_limit),
      }),
    };

    console.log("📤 CreateCoupon payload:", payload);
    setSubmitting(true);

    try {
      const res = await fetch(`${BASE_URL}/CreateCoupon`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("SLNAdminToken") ?? "",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      console.log("✅ CreateCoupon response:", data);
      if (!res.ok) throw new Error(data?.message ?? `HTTP ${res.status}`);

      await Swal.fire({
        title: "Coupon Created!",
        text: data?.message ?? "Coupon has been created successfully.",
        icon: "success",
        confirmButtonColor: "#198754",
        confirmButtonText: "Go to Coupons",
        customClass: { popup: "rounded-4", confirmButton: "px-4 rounded-3" },
      });

      navigate("/manage/all-coupons");
    } catch (err) {
      console.error("❌ CreateCoupon error:", err);
      await Swal.fire({
        title: "Failed to Create",
        text: err.message || "Something went wrong.",
        icon: "error",
        confirmButtonColor: "#dc3545",
        customClass: { popup: "rounded-4", confirmButton: "px-4 rounded-3" },
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ── UI helpers ────────────────────────────────────────────────────────────
  const SectionHead = ({ icon, title }) => (
    <div className="col-12 mt-4 mb-1">
      <h6 className="fw-semibold text-dark d-flex align-items-center gap-2 mb-0">
        <i className={`bi ${icon} text-primary`}></i> {title}
      </h6>
      <hr className="mt-1 mb-0" />
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Header />

      <section>
        <div className="container py-3">
          <div className="row">
            <div className="col-12">
              <div className="pt-1 d-flex flex-column flex-md-row gap-3 align-items-md-center justify-content-between">
                <div>
                  <h4 className="mb-0 lh-1 fw-medium text-dark">Add Coupon</h4>
                  <p className="m-0 lh-sm text-secondary ls-sm">
                    Create a new discount coupon
                  </p>
                </div>
                <div>
                  <span
                    onClick={() => navigate(-1)}
                    className="btn btn-outline-secondary btn-sm px-3 rounded-pill"
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-arrow-left me-1"></i> Back
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <form
              className="row g-2 needs-validation"
              noValidate
              onSubmit={handleSubmit}
            >
              {/* ── Coupon Identity ── */}
              <SectionHead
                icon="bi-ticket-perforated"
                title="Coupon Identity"
              />
              <div className="col-12">
                <div className="border rounded-4 p-4 bg-light">
                  <div className="row g-2">
                    {/* Code */}
                    <div className="col-lg-3 col-md-4 col-12">
                      <label className="form-label small">
                        Coupon Code{" "}
                        <span className="text-danger fw-bold">*</span>
                      </label>
                      <input
                        type="text"
                        name="code"
                        className="form-control form-control-sm text-uppercase"
                        placeholder="e.g. WINTER26"
                        value={form.code}
                        onChange={handleField}
                        required
                        style={{ letterSpacing: "0.06em", fontWeight: 600 }}
                      />
                      <div className="invalid-feedback">
                        Please enter a coupon code.
                      </div>
                      <div className="form-text">
                        Will be auto-uppercased on submit.
                      </div>
                    </div>

                    {/* Description */}
                    <div className="col-lg-5 col-md-8 col-12">
                      <label className="form-label small">
                        Description{" "}
                        <span className="text-danger fw-bold">*</span>
                      </label>
                      <input
                        type="text"
                        name="description"
                        className="form-control form-control-sm"
                        placeholder="e.g. Winter Sale 2026"
                        value={form.description}
                        onChange={handleField}
                        required
                      />
                      <div className="invalid-feedback">
                        Please enter a description.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Discount Settings ── */}
              <SectionHead icon="bi-percent" title="Discount Settings" />
              <div className="col-12">
                <div className="border rounded-4 p-4 bg-light">
                  <div className="row g-2">
                    {/* Discount Type */}
                    <div className="col-lg-3 col-md-4 col-6">
                      <label className="form-label small">
                        Discount Type{" "}
                        <span className="text-danger fw-bold">*</span>
                      </label>
                      <select
                        name="discount_type"
                        className="form-select form-select-sm"
                        value={form.discount_type}
                        onChange={handleField}
                        required
                      >
                        <option value="percentage">Percentage (%)</option>
                        <option value="flat">Flat (₹)</option>
                      </select>
                    </div>

                    {/* Discount Value */}
                    <div className="col-lg-2 col-md-3 col-6">
                      <label className="form-label small">
                        Discount Value{" "}
                        <span className="text-danger fw-bold">*</span>
                      </label>
                      <div className="input-group input-group-sm">
                        <span className="input-group-text">
                          {form.discount_type === "percentage" ? "%" : "₹"}
                        </span>
                        <input
                          type="number"
                          name="discount_value"
                          className="form-control form-control-sm"
                          placeholder={
                            form.discount_type === "percentage" ? "25" : "200"
                          }
                          min="1"
                          max={
                            form.discount_type === "percentage" ? "100" : undefined
                          }
                          value={form.discount_value}
                          onChange={handleField}
                          required
                        />
                      </div>
                      <div className="invalid-feedback">Required.</div>
                    </div>

                    {/* Min Purchase */}
                    <div className="col-lg-2 col-md-3 col-6">
                      <label className="form-label small">
                        Min. Purchase (₹)
                      </label>
                      <input
                        type="number"
                        name="min_purchase_amount"
                        className="form-control form-control-sm"
                        placeholder="100"
                        min="0"
                        value={form.min_purchase_amount}
                        onChange={handleField}
                      />
                    </div>

                    {/* Max Discount — only relevant for percentage */}
                    {form.discount_type === "percentage" && (
                      <div className="col-lg-2 col-md-3 col-6">
                        <label className="form-label small">
                          Max Discount (₹)
                        </label>
                        <input
                          type="number"
                          name="max_discount_amount"
                          className="form-control form-control-sm"
                          placeholder="300"
                          min="0"
                          value={form.max_discount_amount}
                          onChange={handleField}
                        />
                        <div className="form-text">Cap on % discount.</div>
                      </div>
                    )}

                    {/* Usage Limit */}
                    <div className="col-lg-2 col-md-3 col-6">
                      <label className="form-label small">Usage Limit</label>
                      <input
                        type="number"
                        name="usage_limit"
                        className="form-control form-control-sm"
                        placeholder="1000"
                        min="1"
                        value={form.usage_limit}
                        onChange={handleField}
                      />
                      <div className="form-text">Leave blank for unlimited.</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Validity Period ── */}
              <SectionHead icon="bi-calendar-range" title="Validity Period" />
              <div className="col-12">
                <div className="border rounded-4 p-4 bg-light">
                  <div className="row g-2">
                    {/* Start Date */}
                    <div className="col-lg-3 col-md-4 col-sm-6 col-12">
                      <label className="form-label small">
                        Start Date{" "}
                        <span className="text-danger fw-bold">*</span>
                      </label>
                      <input
                        type="date"
                        name="start_date"
                        className="form-control form-control-sm"
                        value={form.start_date}
                        onChange={handleField}
                        required
                      />
                      <div className="invalid-feedback">
                        Please select a start date.
                      </div>
                    </div>

                    {/* End Date */}
                    <div className="col-lg-3 col-md-4 col-sm-6 col-12">
                      <label className="form-label small">
                        End Date{" "}
                        <span className="text-danger fw-bold">*</span>
                      </label>
                      <input
                        type="date"
                        name="end_date"
                        className="form-control form-control-sm"
                        min={form.start_date || undefined}
                        value={form.end_date}
                        onChange={handleField}
                        required
                      />
                      <div className="invalid-feedback">
                        Please select an end date.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Preview card ── */}
              {form.code && (
                <>
                  <SectionHead icon="bi-eye" title="Preview" />
                  <div className="col-12">
                    <div className="border rounded-4 p-4 bg-light">
                      <div
                        className="d-inline-flex flex-column gap-1 border rounded-3 p-3 bg-white"
                        style={{ minWidth: 260 }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="badge bg-dark text-white px-3 py-2 rounded-3 fs-6 fw-semibold"
                            style={{ letterSpacing: "0.08em" }}
                          >
                            {form.code.toUpperCase()}
                          </span>
                          <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1 rounded-3 small">
                            Preview
                          </span>
                        </div>
                        {form.description && (
                          <p className="mb-0 small text-secondary">
                            {form.description}
                          </p>
                        )}
                        {form.discount_value && (
                          <p className="mb-0 fw-semibold text-primary">
                            {form.discount_type === "percentage"
                              ? `${form.discount_value}% off`
                              : `₹${form.discount_value} off`}
                            {form.discount_type === "percentage" &&
                              form.max_discount_amount && (
                                <span className="text-secondary fw-normal ms-1 small">
                                  (max ₹{form.max_discount_amount})
                                </span>
                              )}
                          </p>
                        )}
                        {(form.start_date || form.end_date) && (
                          <p className="mb-0 small text-secondary">
                            {form.start_date} → {form.end_date || "…"}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* ── Submit ── */}
              <div className="col-12 mt-4">
                <button
                  className="btn btn-success px-6 fw-normal py-1 fs-5 ls-sm d-inline-flex align-items-center gap-2"
                  type="submit"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                      />
                      Creating…
                    </>
                  ) : (
                    <>
                      <i className="bi bi-plus-circle"></i> Create Coupon
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

export default AddCoupon;
